import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { legalDocumentsApi } from '@/lib/pillarApi';
import { fromSystemDateTimeLocalInput } from '@/lib/system-time-zone-datetime';
import { SystemTimeZoneDateTimeField } from './SystemTimeZoneDateTimeField';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import type { CreateLegalDocumentRequest } from '@pillar-portal/api-types';
import { LegalDocumentPurpose } from '@pillar-portal/database/dist/frontend';
import { formatLegalDocumentPurpose } from './legal-document-purpose-format';

type EditorMode = 'paste' | 'upload';

const DateTimeLocalSchema = z.string().min(1);
const LegalDocumentPurposeSchema = z.enum(LegalDocumentPurpose);

function toIsoFromDatetimeLocal(value: string): string {
    const parseResult = DateTimeLocalSchema.safeParse(value);
    if (!parseResult.success) {
        throw new Error('Datetime is required');
    }
    const timestamp = fromSystemDateTimeLocalInput(parseResult.data);
    if (timestamp === null) {
        throw new Error('Invalid datetime value');
    }
    return timestamp;
}

export function LegalDocumentCreatePage() {
    const breadcrumbNavigate = useBreadcrumbNavigation();

    const [title, setTitle] = useState<string>('');
    const [version, setVersion] = useState<string>('');
    const [contentType, setContentType] = useState<string>('text/html');
    const [inForceSince, setInForceSince] = useState<string>('');
    const [inForceTo, setInForceTo] = useState<string>('');
    const [legalDocumentPurpose, setLegalDocumentPurpose] = useState<LegalDocumentPurpose>(
        LegalDocumentPurpose.USER_AGREEMENT
    );
    const [acceptanceValidityDurationDays, setAcceptanceValidityDurationDays] =
        useState<string>('');
    const [acceptanceExpires, setAcceptanceExpires] = useState<boolean>(false);
    const [editorMode, setEditorMode] = useState<EditorMode>('paste');
    const [text, setText] = useState<string>('');
    const [sourceFilename, setSourceFilename] = useState<string | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Existing titles are suggested so a new version keeps its title: user
    // agreements with the same title override each other across Organizations.
    const existingTitlesQuery = useQuery({
        queryKey: ['legalDocumentTitles'],
        queryFn: async () => {
            const response = await legalDocumentsApi.searchLegalDocumentsPage({ limit: 100 });
            return [
                ...new Set(
                    response.legal_documents.map((document) => document.legal_document_title)
                ),
            ].sort((left, right) => left.localeCompare(right));
        },
        staleTime: 60_000,
    });

    const createMutation = useMutation({
        mutationFn: async (data: CreateLegalDocumentRequest) => {
            return await legalDocumentsApi.createLegalDocument(data);
        },
        onSuccess: (result) => {
            breadcrumbNavigate({
                source: 'terminal',
                target: {
                    url: `/legal-documents/${result.legal_document_uuid}`,
                    title: 'Legal Document Details',
                },
            });
        },
        onError: (err) => {
            setErrorMessage(err instanceof Error ? err.message : 'Create failed');
        },
    });

    const missingRequiredFields: ReadonlyArray<string> = useMemo(() => {
        const missing: Array<string> = [];
        if (title.trim().length === 0) missing.push('Title');
        if (version.trim().length === 0) missing.push('Version');
        if (contentType.trim().length === 0) missing.push('Content type');
        if (inForceSince.trim().length === 0) missing.push('In force since');
        if (text.trim().length === 0) missing.push('Text');
        if (
            legalDocumentPurpose === LegalDocumentPurpose.ORGANIZATION_AGREEMENT &&
            acceptanceExpires &&
            (!Number.isInteger(Number(acceptanceValidityDurationDays)) ||
                Number(acceptanceValidityDurationDays) <= 0)
        ) {
            missing.push('Validity duration');
        }
        return missing;
    }, [
        acceptanceValidityDurationDays,
        acceptanceExpires,
        contentType,
        inForceSince,
        legalDocumentPurpose,
        text,
        title,
        version,
    ]);

    const canSubmit: boolean = useMemo(() => {
        if (createMutation.isPending) {
            return false;
        }
        return missingRequiredFields.length === 0;
    }, [createMutation.isPending, missingRequiredFields.length]);

    const onUploadFileSelected = async (file: File): Promise<void> => {
        setSourceFilename(file.name);
        const fileText: string = await file.text();
        setText(fileText);
    };

    return (
        <div className="p-6 max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">Create Legal Document</h1>
                <Button
                    variant="outline"
                    onClick={() => {
                        breadcrumbNavigate({
                            source: 'panel',
                            target: {
                                url: '/legal-documents',
                                title: 'Legal Documents',
                            },
                        });
                    }}
                >
                    Back
                </Button>
            </div>

            {errorMessage && (
                <Card className="mb-4 border-red-300">
                    <CardHeader>
                        <CardTitle className="text-red-700">Error</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm">{errorMessage}</p>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Metadata</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                            aria-label="Title"
                            list="legal-document-existing-titles"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        <datalist id="legal-document-existing-titles">
                            {(existingTitlesQuery.data ?? []).map((existingTitle) => (
                                <option key={existingTitle} value={existingTitle} />
                            ))}
                        </datalist>
                        <p className="text-xs text-gray-600">
                            Reuse the title of an existing agreement to publish a new version of it.
                        </p>
                    </div>
                    <div className="space-y-2">
                        <Label>Version</Label>
                        <Input
                            aria-label="Version"
                            value={version}
                            onChange={(e) => setVersion(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Content type</Label>
                        <Input
                            aria-label="Content type"
                            value={contentType}
                            onChange={(e) => setContentType(e.target.value)}
                            placeholder="text/html"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <SystemTimeZoneDateTimeField
                            id="legal-document-create-start"
                            label="In force since"
                            value={inForceSince}
                            onChange={setInForceSince}
                        />
                        <SystemTimeZoneDateTimeField
                            id="legal-document-create-end"
                            label="In force to"
                            value={inForceTo}
                            onChange={setInForceTo}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Purpose</Label>
                        <select
                            aria-label="Purpose"
                            className="w-full rounded-md border px-3 py-2"
                            value={legalDocumentPurpose}
                            onChange={(event) => {
                                const nextPurpose = LegalDocumentPurposeSchema.parse(
                                    event.target.value
                                );
                                setLegalDocumentPurpose(nextPurpose);
                                if (nextPurpose !== LegalDocumentPurpose.ORGANIZATION_AGREEMENT) {
                                    setAcceptanceExpires(false);
                                    setAcceptanceValidityDurationDays('');
                                }
                            }}
                        >
                            <option value={LegalDocumentPurpose.USER_AGREEMENT}>
                                User agreement (every user accepts, e.g. Terms, EULA, Privacy)
                            </option>
                            <option value={LegalDocumentPurpose.ORGANIZATION_AGREEMENT}>
                                Organization agreement (a PRINCIPAL signs)
                            </option>
                        </select>
                    </div>
                    {legalDocumentPurpose === LegalDocumentPurpose.ORGANIZATION_AGREEMENT ? (
                        <div className="space-y-3">
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    aria-label="Acceptance expires"
                                    type="checkbox"
                                    checked={acceptanceExpires}
                                    onChange={(event) => {
                                        setAcceptanceExpires(event.target.checked);
                                        if (!event.target.checked) {
                                            setAcceptanceValidityDurationDays('');
                                        }
                                    }}
                                />
                                Acceptance expires after a fixed number of days
                            </label>
                            {acceptanceExpires ? (
                                <div className="space-y-2">
                                    <Label>Acceptance validity duration in days</Label>
                                    <Input
                                        aria-label="Acceptance validity duration in days"
                                        type="number"
                                        min={1}
                                        step={1}
                                        value={acceptanceValidityDurationDays}
                                        onChange={(event) =>
                                            setAcceptanceValidityDurationDays(event.target.value)
                                        }
                                    />
                                </div>
                            ) : (
                                <p className="text-sm text-gray-600">
                                    Acceptance remains valid indefinitely for this exact version.
                                </p>
                            )}
                        </div>
                    ) : null}
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardHeader>
                    <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                    <div>Title: {title.trim() || 'Not entered'}</div>
                    <div>Version: {version.trim() || 'Not entered'}</div>
                    <div>Purpose: {formatLegalDocumentPurpose(legalDocumentPurpose)}</div>
                    <div>Content type: {contentType.trim() || 'Not entered'}</div>
                    <div>
                        Acceptance validity:{' '}
                        {legalDocumentPurpose !== LegalDocumentPurpose.ORGANIZATION_AGREEMENT
                            ? 'Not applicable'
                            : acceptanceExpires
                              ? `${acceptanceValidityDurationDays || 'Not entered'} days`
                              : 'Indefinite'}
                    </div>
                    <div>Source: {sourceFilename ?? 'Pasted content'}</div>
                    <p className="pt-2 text-xs text-gray-600">
                        Purpose, validity, content, and version identity cannot be edited after
                        creation. Create a new version to change them.
                    </p>
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardHeader>
                    <CardTitle className="text-base">Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                checked={editorMode === 'paste'}
                                onChange={() => setEditorMode('paste')}
                            />
                            Paste
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                checked={editorMode === 'upload'}
                                onChange={() => setEditorMode('upload')}
                            />
                            Upload
                        </label>
                    </div>

                    {editorMode === 'upload' ? (
                        <div className="space-y-2">
                            <Label>Upload file</Label>
                            <Input
                                type="file"
                                onChange={(e) => {
                                    const file: File | null = e.target.files?.item(0) ?? null;
                                    if (!file) {
                                        return;
                                    }
                                    onUploadFileSelected(file).catch((err) => {
                                        setErrorMessage(
                                            err instanceof Error ? err.message : 'File read failed'
                                        );
                                    });
                                }}
                            />
                        </div>
                    ) : null}

                    <div className="space-y-2">
                        <Label>Text</Label>
                        <textarea
                            aria-label="Text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="w-full h-[40vh] border rounded p-2 font-mono text-sm"
                        />
                    </div>
                </CardContent>
            </Card>

            <div className="mt-4 flex items-center justify-end gap-3">
                {!canSubmit && missingRequiredFields.length > 0 && (
                    <div className="mr-auto text-sm text-gray-600">
                        Required: {missingRequiredFields.join(', ')}
                    </div>
                )}
                <Button
                    disabled={!canSubmit}
                    onClick={() => {
                        setErrorMessage(null);
                        const payload: CreateLegalDocumentRequest = {
                            legal_document_title: title.trim(),
                            legal_document_version: version.trim(),
                            legal_document_text: text,
                            legal_document_content_type: contentType.trim(),
                            in_force_start: toIsoFromDatetimeLocal(inForceSince),
                            in_force_end:
                                inForceTo.trim().length === 0
                                    ? null
                                    : toIsoFromDatetimeLocal(inForceTo),
                            legal_document_purpose: legalDocumentPurpose,
                            acceptance_validity_duration_days:
                                legalDocumentPurpose === LegalDocumentPurpose.ORGANIZATION_AGREEMENT
                                    ? acceptanceExpires
                                        ? Number(acceptanceValidityDurationDays)
                                        : null
                                    : null,
                            source_filename: sourceFilename,
                        };
                        createMutation.mutate(payload);
                    }}
                >
                    {createMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </div>
    );
}
